import type { Breakpoint } from '@mui/material';
import type { NavItemBaseProps } from 'src/components/nav-section';

import { Box, Stack, Divider, useTheme, useMediaQuery } from '@mui/material';

import { varAlpha } from 'src/theme/styles';

import { Scrollbar } from 'src/components/scrollbar';
import { NavSectionVertical } from 'src/components/nav-section';

import { NavHorizontal } from '../dashboard/nav-horizontal';

// ----------------------------------------------------------------------

export type ModuleLayoutProps = {
  children: React.ReactNode;
  data: {
    subheader?: string;
    items: NavItemBaseProps[];
  }[];
  navTopComponent?: React.ReactNode;
};

const layoutQuery: Breakpoint = 'lg';

export function ModuleLayout({ children, data, navTopComponent }: ModuleLayoutProps) {
  const theme = useTheme();

  const { palette } = theme.vars;

  const isMobile = useMediaQuery(theme.breakpoints.down(layoutQuery));

  return (
    <Stack position="relative">
      {!isMobile && (
        <Divider
          sx={{
            width: 1,
            borderStyle: 'dashed',
            position: 'absolute',
            top: 0,
            zIndex: 'var(--layout-nav-zIndex)',
          }}
        />
      )}
      <Stack position="relative" direction={isMobile ? 'column' : 'row'}>
        {isMobile && (
          <NavHorizontal
            data={data}
            layoutQuery="xs"
            cssVars={{
              layout: {
                '--layout-nav-bg': palette.background.default,
                '--layout-nav-horizontal-bg': varAlpha(palette.background.defaultChannel, 0.8),
                '--layout-nav-border-color': varAlpha(palette.grey['500Channel'], 0.12),
                '--layout-nav-text-primary-color': palette.text.primary,
                '--layout-nav-text-secondary-color': palette.text.secondary,
                '--layout-nav-text-disabled-color': palette.text.disabled,
              },
              section: {},
            }}
          />
        )}
        {!isMobile && <NavSection data={data} navTopComponent={navTopComponent} />}
        <Content isMobile={isMobile}>{children}</Content>
      </Stack>
    </Stack>
  );
}
type NavSectionProps = {
  data: {
    subheader?: string;
    items: NavItemBaseProps[];
  }[];
  navTopComponent?: React.ReactNode;
};
const NavSection = ({ data, navTopComponent }: NavSectionProps) => {
  const theme = useTheme();
  const { palette } = theme.vars;
  return (
    <Box
      sx={{
        height: 'calc(100vh - var(--layout-header-desktop-height))',
        display: 'flex',
        position: 'fixed',
        flexDirection: 'column',
        bgcolor: 'var(--layout-nav-bg)',
        // zIndex: 'var(--layout-nav-zIndex)',
        width: 'var(--layout-nav-vertical-width)',
        borderRight: `1px solid var(--layout-nav-border-color, ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)})`,
        transition: theme.transitions.create(['width'], {
          easing: 'var(--layout-transition-easing)',
          duration: 'var(--layout-transition-duration)',
        }),
      }}
    >
      <Scrollbar fillContent>
        {navTopComponent && (
          <>
            <Box sx={{ p: 2 }}>{navTopComponent}</Box>
            <Divider sx={{ borderStyle: 'dashed', mx: 2, mb: 1 }} />
          </>
        )}
        <NavSectionVertical
          data={data}
          sx={{ p: 2 }}
          cssVars={{
            layout: {
              '--layout-nav-bg': palette.background.default,
              '--layout-nav-horizontal-bg': varAlpha(palette.background.defaultChannel, 0.8),
              '--layout-nav-border-color': varAlpha(palette.grey['500Channel'], 0.12),
              '--layout-nav-text-primary-color': palette.text.primary,
              '--layout-nav-text-secondary-color': palette.text.secondary,
              '--layout-nav-text-disabled-color': palette.text.disabled,
            },
            section: {},
          }}
        />
      </Scrollbar>
    </Box>
  );
};

type ContentProps = {
  children: React.ReactNode;
  isMobile?: boolean;
};
const Content = ({ children, isMobile }: ContentProps) => (
  <Box
    sx={{
      height: `calc(100vh - ${
        isMobile
          ? 'calc(var(--layout-header-mobile-height) + var(--layout-header-mobile-height))'
          : 'var(--layout-header-desktop-height)'
      })`,
      display: 'flex',
      flex: 1,
      position: 'absolute',
      left: isMobile ? 0 : 'var(--layout-nav-vertical-width)',
      top: isMobile ? 'var(--layout-header-mobile-height)' : 0,
      width: isMobile ? '100%' : `calc(100% - var(--layout-nav-vertical-width))`,
    }}
  >
    <Scrollbar fillContent>{children}</Scrollbar>
  </Box>
);
